import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ForexToolsPanel } from "@/components/ForexTools";
import { CommunityPanel } from "@/components/Community";
import { ManageSubscription } from "@/components/ManageSubscription";
import { ChartAnalyzer } from "@/components/ChartAnalyzer";
import { TradeJournal } from "@/components/TradeJournal";
import { EconomicCalendar } from "@/components/EconomicCalendar";
import { NotificationsBell } from "@/components/NotificationsBell";
import { ThemeToggle } from "@/components/ThemeToggle";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Minus, Loader2, LogOut, Target, Trophy,
  Activity, Percent, Layers, Cpu, CheckCircle2, XCircle, Clock,
  Calculator, Users, CreditCard, BookOpen, ScanLine, CalendarDays,
} from "lucide-react";

const biasMeta = {
  BULLISH: { c: "text-gain", bg: "bg-gain/12 border-gain/30", Icon: TrendingUp },
  BEARISH: { c: "text-loss", bg: "bg-loss/12 border-loss/30", Icon: TrendingDown },
  NEUTRAL: { c: "text-amber", bg: "bg-amber/12 border-amber/30", Icon: Minus },
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

const StatCard = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <div data-testid={testId} className="card-surface rounded-xl p-5">
    <div className="flex items-center justify-between">
      <span className="font-data text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <Icon className={`h-4 w-4 ${accent || "text-brand"}`} />
    </div>
    <div className={`font-data text-3xl font-bold mt-3 ${accent || "text-white"}`}>{value}</div>
    {sub && <div className="font-data text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

const OutcomeButtons = ({ current, onSet }) => {
  const opts = [
    { v: "win", label: "Win", Icon: CheckCircle2, on: "bg-gain/15 text-gain border-gain/40" },
    { v: "loss", label: "Loss", Icon: XCircle, on: "bg-loss/15 text-loss border-loss/40" },
    { v: "pending", label: "Open", Icon: Clock, on: "bg-amber/15 text-amber border-amber/40" },
  ];
  return (
    <div className="flex gap-1.5" data-testid="analysis-outcome-toggle">
      {opts.map((o) => (
        <button
          key={o.v}
          data-testid={`outcome-${o.v}-btn`}
          onClick={() => onSet(o.v)}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md border font-data text-[10px] uppercase tracking-wider transition-colors ${
            current === o.v ? o.on : "text-slate-500 border-white/10 hover:border-white/25"
          }`}
        >
          <o.Icon className="h-3 w-3" /> {o.label}
        </button>
      ))}
    </div>
  );
};

const AnalysisRow = ({ a, onOutcome }) => {
  const r = a.result || {};
  const bm = biasMeta[String(r.bias).toUpperCase()] || biasMeta.NEUTRAL;
  return (
    <motion.div
      data-testid="dashboard-analysis-card"
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="card-surface rounded-xl p-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <span className="font-data text-sm font-semibold text-white">{r.pair || "Chart"}</span>
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border font-data text-[10px] font-semibold ${bm.bg} ${bm.c}`}>
            <bm.Icon className="h-3 w-3" /> {r.bias} · {r.confidence}%
          </span>
          <span className="font-data text-[10px] px-2 py-0.5 rounded bg-brand/12 text-brand border border-brand/25">{r.order_type}</span>
        </div>
        <span className="font-data text-[11px] text-slate-500">{fmtDate(a.created_at)}</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        {[
          { k: "Entry", v: r.entry },
          { k: "SL", v: r.stop_loss, c: "text-loss" },
          { k: "TP1", v: r.take_profit_1, c: "text-gain" },
          { k: "TP2", v: r.take_profit_2, c: "text-gain" },
          { k: "R:R", v: r.risk_reward, c: "text-brand" },
        ].map((m) => (
          <div key={m.k} className="rounded-md bg-[#0C1017] border border-white/8 px-2.5 py-1.5">
            <div className="font-data text-[9px] uppercase tracking-widest text-slate-500">{m.k}</div>
            <div className={`font-data text-sm font-semibold mt-0.5 ${m.c || "text-white"}`}>{m.v || "—"}</div>
          </div>
        ))}
      </div>

      {r.summary && <p className="text-sm text-slate-400 leading-relaxed mb-3 line-clamp-2">{r.summary}</p>}

      <div className="flex items-center justify-between pt-3 border-t border-white/5">
        <span className="font-data text-[10px] uppercase tracking-widest text-slate-500">Trade outcome</span>
        <OutcomeButtons current={a.outcome || "pending"} onSet={(v) => onOutcome(a.id, v)} />
      </div>
    </motion.div>
  );
};

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(true);
  const [tab, setTab] = useState("overview");

  const loadAll = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([api.get("/analyze/stats"), api.get("/analyze/history")]);
      setStats(s.data);
      setHistory(h.data);
    } catch (e) {
      console.error("Failed to load dashboard stats/history:", e);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/", { replace: true }); return; }
    loadAll();
  }, [user, loading, navigate, loadAll]);

  const setOutcome = async (id, outcome) => {
    setHistory((prev) => prev.map((a) => (a.id === id ? { ...a, outcome } : a)));
    try {
      await api.patch(`/analyze/history/${id}/outcome`, { outcome });
      const s = await api.get("/analyze/stats");
      setStats(s.data);
    } catch (e) {
      toast.error("Could not update outcome");
      loadAll();
    }
  };

  if (loading || (!user && !loading) || busy) {
    return (
      <div className="min-h-screen bg-[#070A0E] grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  const initials = (user.name || "T").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const donut = [
    { name: "Wins", value: stats?.wins || 0, color: "#00D4AA" },
    { name: "Losses", value: stats?.losses || 0, color: "#FF4757" },
    { name: "Open", value: stats?.pending || 0, color: "#F59E0B" },
  ];
  const hasDecided = (stats?.wins || 0) + (stats?.losses || 0) > 0;

  return (
    <div className="min-h-screen bg-[#070A0E] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#070A0E]/90 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="dashboard-brand-home" className="flex items-center gap-2.5 group">
            <div className="h-9 w-9 rounded-md bg-brand/15 border border-brand/40 grid place-items-center group-hover:border-brand transition-colors">
              <TrendingUp className="h-5 w-5 text-brand" />
            </div>
            <div className="leading-none text-left">
              <div className="font-heading font-extrabold tracking-tight text-[15px]">NETROY</div>
              <div className="font-data text-[9px] uppercase tracking-[0.25em] text-slate-500">Trader Terminal</div>
            </div>
          </button>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <NotificationsBell />
            <button onClick={() => { logout(); navigate("/"); }} data-testid="dashboard-logout-btn"
                    className="h-9 w-9 grid place-items-center rounded-md border border-white/10 text-slate-400 hover:text-loss hover:border-loss/40 transition-colors">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Profile */}
        <div className="flex items-center gap-4 mb-8">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover border border-white/10" />
          ) : (
            <span className="h-14 w-14 rounded-full bg-brand/15 text-brand grid place-items-center font-data text-lg font-bold border border-brand/30">{initials}</span>
          )}
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{user.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-data text-xs text-slate-500">{user.email}</span>
              <span className="font-data text-[9px] uppercase tracking-widest px-2 py-0.5 rounded bg-brand/15 text-brand border border-brand/30">
                {user.role === "admin" ? "Admin Tier" : user.plan === "elite" ? "Elite Member" : user.plan === "pro" ? "Pro Member" : "Free Plan"}
              </span>
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList data-testid="dashboard-tabs" className="bg-[#0C1017] border border-white/10 mb-8 flex-wrap h-auto p-1">
            <TabsTrigger value="overview" data-testid="dashboard-tab-overview" className="data-[state=active]:bg-brand data-[state=active]:text-white text-slate-400 font-data text-xs uppercase tracking-wider">Overview</TabsTrigger>
            <TabsTrigger value="analyzer" data-testid="dashboard-tab-analyzer" className="data-[state=active]:bg-brand data-[state=active]:text-white text-slate-400 font-data text-xs uppercase tracking-wider">NC-Analyzer</TabsTrigger>
            <TabsTrigger value="journal" data-testid="dashboard-tab-journal" className="data-[state=active]:bg-brand data-[state=active]:text-white text-slate-400 font-data text-xs uppercase tracking-wider">Journal</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="dashboard-tab-calendar" className="data-[state=active]:bg-brand data-[state=active]:text-white text-slate-400 font-data text-xs uppercase tracking-wider">Calendar</TabsTrigger>
            <TabsTrigger value="tools" data-testid="dashboard-tab-tools" className="data-[state=active]:bg-brand data-[state=active]:text-white text-slate-400 font-data text-xs uppercase tracking-wider">Forex-Tools</TabsTrigger>
            <TabsTrigger value="community" data-testid="dashboard-tab-community" className="data-[state=active]:bg-brand data-[state=active]:text-white text-slate-400 font-data text-xs uppercase tracking-wider">Community</TabsTrigger>
            <TabsTrigger value="subscription" data-testid="dashboard-tab-subscription" className="data-[state=active]:bg-brand data-[state=active]:text-white text-slate-400 font-data text-xs uppercase tracking-wider">Subscription</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div data-testid="dashboard-win-rate-card" className="card-surface rounded-xl p-5 relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="font-data text-[10px] uppercase tracking-widest text-slate-500">Win Rate</span>
              <Trophy className="h-4 w-4 text-amber" />
            </div>
            <div className="font-data text-4xl font-black mt-3 text-white">{stats?.win_rate ?? 0}<span className="text-xl text-slate-500">%</span></div>
            <div className="font-data text-[11px] text-slate-500 mt-1">{stats?.wins || 0}W · {stats?.losses || 0}L · {stats?.pending || 0} open</div>
            <div className="absolute -right-6 -bottom-6 h-24 w-24 rounded-full bg-amber/5 blur-xl" />
          </div>
          <StatCard testId="dashboard-total-card" icon={Activity} label="Total Analyses" value={stats?.total ?? 0} sub={`Top pair · ${stats?.top_pair || "—"}`} />
          <StatCard testId="dashboard-confidence-card" icon={Percent} label="Avg Confidence" value={`${stats?.avg_confidence ?? 0}%`} sub="AI conviction" accent="text-brand" />
          <StatCard testId="dashboard-rr-card" icon={Target} label="Avg Risk : Reward" value={`1:${stats?.avg_rr ?? 0}`} sub="Planned targets" accent="text-gain" />
        </div>

        {/* Chart + bias */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
          <div className="card-surface rounded-xl p-6">
            <h3 className="font-heading text-lg font-bold mb-4">Outcome Split</h3>
            {hasDecided || (stats?.pending || 0) > 0 ? (
              <div className="flex items-center gap-6">
                <div className="h-36 w-36 relative">
                  <PieChart width={144} height={144}>
                    <Pie data={donut} dataKey="value" cx={68} cy={68} innerRadius={44} outerRadius={64} paddingAngle={3} stroke="none">
                      {donut.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                  </PieChart>
                  <div className="absolute inset-0 grid place-items-center pointer-events-none">
                    <span className="font-data text-xl font-bold text-white">{stats?.win_rate ?? 0}%</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {donut.map((d) => (
                    <div key={d.name} className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} />
                      <span className="font-data text-xs text-slate-400">{d.name}</span>
                      <span className="font-data text-xs text-white font-semibold ml-auto">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500">Mark your trades as Win/Loss to see your split.</p>
            )}
          </div>

          <div className="card-surface rounded-xl p-6 lg:col-span-2">
            <h3 className="font-heading text-lg font-bold mb-4">Bias Breakdown</h3>
            <div className="space-y-4">
              {["BULLISH", "BEARISH", "NEUTRAL"].map((b) => {
                const bm = biasMeta[b];
                const count = stats?.bias_breakdown?.[b] || 0;
                const pct = stats?.total ? Math.round((count / stats.total) * 100) : 0;
                return (
                  <div key={b}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`inline-flex items-center gap-1.5 font-data text-xs ${bm.c}`}>
                        <bm.Icon className="h-3.5 w-3.5" /> {b}
                      </span>
                      <span className="font-data text-xs text-slate-400">{count} · {pct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500"
                           style={{ width: `${pct}%`, background: b === "BULLISH" ? "#00D4AA" : b === "BEARISH" ? "#FF4757" : "#F59E0B" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* History */}
        <div className="flex items-center gap-2 mb-5">
          <Layers className="h-5 w-5 text-brand" />
          <h2 className="font-heading text-xl font-bold">Saved Analyses</h2>
          <span className="font-data text-xs text-slate-500">({history.length})</span>
        </div>

        {history.length === 0 ? (
          <div className="card-surface rounded-xl py-16 flex flex-col items-center text-center">
            <div className="h-14 w-14 rounded-xl border border-white/10 bg-white/5 grid place-items-center mb-4">
              <Cpu className="h-7 w-7 text-slate-500" />
            </div>
            <p className="text-slate-400 text-sm max-w-sm">You haven't run any chart analyses yet. Head to the AI Chart Analyzer to generate your first institutional trade breakdown.</p>
            <a href="/#analyzer" className="mt-5">
              <Button className="bg-brand hover:bg-brand-hover text-white font-semibold gap-2"><Cpu className="h-4 w-4" /> Run First Analysis</Button>
            </a>
          </div>
        ) : (
          <div data-testid="dashboard-analyses-list" className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {history.map((a) => <AnalysisRow key={a.id} a={a} onOutcome={setOutcome} />)}
          </div>
        )}
          </TabsContent>

          <TabsContent value="analyzer">
            <div className="flex items-center gap-2 mb-5">
              <ScanLine className="h-5 w-5 text-brand" />
              <h2 className="font-heading text-xl font-bold">Netroy Chart Analyzer</h2>
            </div>
            <ChartAnalyzer onRequireAuth={() => {}} onUpgrade={() => setTab("subscription")} embedded />
          </TabsContent>

          <TabsContent value="journal">
            <TradeJournal />
          </TabsContent>

          <TabsContent value="calendar">
            <div className="flex items-center gap-2 mb-5">
              <CalendarDays className="h-5 w-5 text-brand" />
              <h2 className="font-heading text-xl font-bold">Economic Calendar</h2>
              <span className="font-data text-xs text-slate-500">· this week</span>
            </div>
            <EconomicCalendar />
          </TabsContent>

          <TabsContent value="tools">
            <div className="flex items-center gap-2 mb-5">
              <Calculator className="h-5 w-5 text-brand" />
              <h2 className="font-heading text-xl font-bold">Forex Tools</h2>
              <span className="font-data text-xs text-slate-500">· live rates</span>
            </div>
            <ForexToolsPanel />
          </TabsContent>

          <TabsContent value="community">
            <div className="flex items-center gap-2 mb-5">
              <Users className="h-5 w-5 text-brand" />
              <h2 className="font-heading text-xl font-bold">Community Floor</h2>
            </div>
            <CommunityPanel onRequireAuth={() => {}} />
          </TabsContent>

          <TabsContent value="subscription">
            <div className="flex items-center gap-2 mb-5">
              <CreditCard className="h-5 w-5 text-brand" />
              <h2 className="font-heading text-xl font-bold">Manage Subscription</h2>
            </div>
            <ManageSubscription />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
